import React, { useState } from 'react';
import { IconArrowForward, IconChevronRight } from '@noths/polaris-client-ribbons-design-system';

import * as styles from './MegaNav.styles';
import { useTracking } from 'client-site-chrome-ui/components/molecules/DepartmentsNav/hooks/useTracking/useTracking';
import type { Level2Item } from 'client-site-chrome-ui/types/departmentsNav';

export interface MegaNavProps {
  departmentTitle: string;
  departmentUrl: string;
  level2Items: Level2Item[];
}

export const MegaNav = ({ departmentTitle, departmentUrl, level2Items }: MegaNavProps) => {
  const [openLevel2Index, setOpenLevel2Index] = useState<number | null>(null);
  const tracking = useTracking();

  const handleLevel2Click = (evt: React.MouseEvent) => {
    if (openLevel2Index === null) {
      return;
    }

    const isKeyboardClick = evt.detail === 0;

    if (isKeyboardClick && level2Items[openLevel2Index]?.items.length) {
      const firstLevel3Item = document.querySelector(
        `li[data-level="3"] a:first-of-type`,
      ) as HTMLAnchorElement;
      if (firstLevel3Item) {
        evt.preventDefault();
        firstLevel3Item.focus();
        return;
      }
    }

    tracking.onClickLink(evt as React.MouseEvent<HTMLAnchorElement>, level2Items[openLevel2Index]);
  };

  return (
    <div css={styles.container}>
      <nav css={styles.navContainer}>
        <ul css={styles.level2}>
          {level2Items.map((level2Item, index) => {
            const linkID = `level2-link-${index}`;

            return (
              <li
                aria-current={index === openLevel2Index}
                id={linkID}
                key={linkID}
                onMouseEnter={() => setOpenLevel2Index(index)}
              >
                {level2Item.url ? (
                  <a
                    href={level2Item.url}
                    onClick={handleLevel2Click}
                    onFocus={() => setOpenLevel2Index(index)}
                  >
                    {level2Item.title}
                    {level2Item.items.length ? <IconChevronRight height={16} width={16} /> : null}
                  </a>
                ) : (
                  <button
                    onClick={handleLevel2Click}
                    onFocus={() => setOpenLevel2Index(index)}
                    type="button"
                  >
                    {level2Item.title}
                    {level2Item.items.length ? <IconChevronRight height={16} width={16} /> : null}
                  </button>
                )}
              </li>
            );
          })}
          <li onMouseEnter={() => setOpenLevel2Index(null)}>
            <a href={departmentUrl} onFocus={() => setOpenLevel2Index(null)}>
              See all {departmentTitle}{' '}
              <IconArrowForward color={`var(--brand-purple-base)`} height={16} width={16} />
            </a>
          </li>
        </ul>
        {openLevel2Index !== null ? (
          <ul aria-labelledby={`level2-link-${openLevel2Index}`} css={styles.level3}>
            {level2Items[openLevel2Index].items.map((level3Item, index) => (
              <li data-level={3} key={index}>
                <a href={level3Item.url} onClick={(evt) => tracking.onClickLink(evt, level3Item)}>
                  {level3Item.title}
                </a>
              </li>
            ))}
          </ul>
        ) : null}
      </nav>
    </div>
  );
};
